import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';

const faqData = [
  {
    q: "Quels sont les signes d'une panne de store électrique ?",
    a: "Les signes courants incluent : le store ne réagit plus à la télécommande, le moteur tourne dans le vide sans déplacer le store, le store s'arrête en milieu de course, ou fait un bruit anormal. Si vous constatez l'un de ces symptômes, contactez-nous pour un diagnostic.",
  },
  {
    q: "Pouvez-vous réparer un store électrique de véranda ?",
    a: "Oui, nous intervenons sur tous les types de stores électriques, y compris les stores de véranda (store sous toiture, store latéral). Nous réparons ou remplaçons le moteur, la toile, les rails de guidage et les systèmes de commande.",
  },
  {
    q: "Proposez-vous l'installation de stores électriques neufs à Lyon ?",
    a: "Oui, en plus du dépannage, nous pouvons fournir et installer des stores électriques neufs : store extérieur déroulant, store de terrasse, store de véranda. Nous vous conseillons sur le modèle le plus adapté à votre configuration.",
  },
];

export default function StoreElectrique() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Store électrique à Lyon : dépannage et pose',
    provider: { '@type': 'HomeAndConstructionBusiness', name: 'DFPRO Lyon', telephone: '+33980803684' },
    areaServed: [{ '@type': 'City', name: 'Lyon' }, { '@type': 'AdministrativeArea', name: 'Métropole de Lyon' }],
    description: 'Store électrique en panne à Lyon ? Dépannage moteur, télécommande, store déroulant. Pose de store neuf. Devis gratuit, garantie 10 ans.',
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({ '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://depanfenetres-lyon.fr/' },
      { '@type': 'ListItem', position: 2, name: 'Réparation store à Lyon', item: 'https://depanfenetres-lyon.fr/reparation-store-lyon/' },
      { '@type': 'ListItem', position: 3, name: 'Store électrique', item: 'https://depanfenetres-lyon.fr/store-electrique-lyon/' },
    ],
  };

  return (
    <>
      <SEOHead
        title="Store électrique à Lyon : dépannage & pose | DFPRO"
        description="Store électrique en panne à Lyon ? Moteur, télécommande, store déroulant. Dépannage et pose de store neuf. 24-48h, 7j/7. Devis gratuit. DFPRO Lyon."
        canonicalUrl="https://depanfenetres-lyon.fr/store-electrique-lyon/"
        ogTitle="Store électrique à Lyon — Dépannage & pose | DFPRO"
        ogDescription="Store extérieur électrique en panne ? Dépannage et pose à Lyon. Devis gratuit, garantie 10 ans."
        jsonLd={{ ...serviceJsonLd, ...faqJsonLd, ...breadcrumbJsonLd }}
      />

      <Header />

      <main>
        <div className="pt-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-xs text-secondary-400">
              <a href="/" className="hover:text-primary-500 transition-colors">Accueil</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <a href="/reparation-store-lyon/" className="hover:text-primary-500 transition-colors">Réparation store à Lyon</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-foreground-700 font-medium">Store électrique</span>
            </nav>
          </div>
        </div>

        <section className="relative w-full min-h-[380px] md:min-h-[420px] flex items-center bg-background-50">
          <div className="absolute inset-0">
            <img
              src="/images/store-elec-hero-2026.jpg"
              alt="Store électrique à Lyon — dépannage par DFPRO Lyon, technicien sur store extérieur motorisé"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/50 via-foreground-900/35 to-foreground-900/50"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 py-14 md:py-16">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading font-bold text-2xl md:text-4xl text-background-50 leading-tight">
                Store électrique à Lyon
              </h1>
              <p className="mt-4 md:mt-5 text-sm md:text-lg text-background-50/90 max-w-2xl mx-auto leading-relaxed">
                Votre store extérieur motorisé est en panne ? Dépannage moteur, télécommande et guidage. Nous posons aussi des stores électriques neufs à Lyon.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="tel:+33980803684" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm md:text-base px-6 py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer shadow-[0_2px_8px_rgba(242,140,40,0.4)]">
                  <i className="ri-phone-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                  Appeler — 09 80 80 36 84
                </a>
              </div>
            </div>
          </div>
        </section>

        <ReassuranceBar />

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
              <div className="lg:col-span-2">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-800 mb-5">
                  Store électrique à Lyon : dépannage et installation
                </h2>
                <div className="space-y-4 text-sm md:text-base text-secondary-500 leading-relaxed">
                  <p>
                    Les stores électriques extérieurs — qu'ils soient déroulants, verticaux ou de type screen — sont des équipements de confort qui protègent efficacement du soleil et des regards. Mais comme tout équipement motorisé, ils peuvent tomber en panne. <strong className="text-foreground-800">DFPRO Lyon intervient pour le dépannage</strong> de tous types de stores électriques à Lyon et dans la métropole.
                  </p>
                  <p>
                    Les pannes les plus fréquentes sur les stores électriques concernent le <strong className="text-foreground-800">moteur</strong> : store qui ne descend plus ou qui ne remonte plus, moteur qui émet un bruit anormal ou qui tourne dans le vide. Le <strong className="text-foreground-800">système de commande</strong> est aussi une source courante de problème : télécommande désynchronisée, récepteur radio défaillant, interrupteur mural qui ne répond plus.
                  </p>
                  <p>
                    Le <strong className="text-foreground-800">mécanisme de descente</strong> peut également s'user : toile qui se bloque en cours de descente, store qui descend de travers (problème de tension ou de guidage latéral), ou toile qui ne s'enroule plus correctement sur le tube. Ces problèmes mécaniques sont souvent liés à l'usure des pièces de guidage ou à un mauvais réglage des fins de course.
                  </p>
                  <p>
                    Notre intervention commence toujours par un <strong className="text-foreground-800">diagnostic complet</strong> de votre store électrique. Nous identifions l'origine de la panne et vous proposons la solution la plus adaptée : réparation sur place avec les pièces détachées dont nous disposons, ou remplacement du store si la réparation n'est pas économiquement pertinente. Nous sommes transparents sur cette évaluation.
                  </p>
                  <p>
                    Pour les <strong className="text-foreground-800">installations neuves</strong>, nous fournissons et posons des stores électriques de qualité : stores extérieurs déroulants, stores screens, stores de véranda. Nous vous conseillons sur le type de toile, la couleur, les options de commande (filaire, radio, domotique). Nous intervenons à Lyon et dans toutes les communes de la Métropole de Lyon. <strong className="text-foreground-800">Garantie 10 ans</strong> sur toutes nos prestations.
                  </p>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 md:p-6 h-fit">
                <h3 className="font-heading font-semibold text-base text-foreground-800 mb-4">Nos services store</h3>
                <nav className="space-y-2">
                  {[
                    { label: 'Réparation store à Lyon (pilier)', href: '/reparation-store-lyon/' },
                    { label: 'Réparation store banne', href: '/reparation-store-banne-lyon/' },
                    { label: 'Store banne bloqué', href: '/store-banne-bloque-lyon/' },
                    { label: 'Motorisation de store', href: '/motorisation-store-lyon/' },
                    { label: 'Réparation BSO', href: '/reparation-bso-lyon/' },
                  ].map((link, idx) => (
                    <a key={idx} href={link.href} className="flex items-center gap-2 text-sm text-secondary-500 hover:text-primary-500 transition-colors py-1.5 cursor-pointer">
                      <i className="ri-arrow-right-s-line text-primary-400 w-4 h-4 flex items-center justify-center"></i>
                      {link.label}
                    </a>
                  ))}
                </nav>
                <div className="mt-5 pt-5 border-t border-background-200">
                  <a href="tel:+33980803684" className="inline-flex items-center justify-center gap-2 w-full bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm py-3 rounded-md transition-colors cursor-pointer">
                    <i className="ri-phone-fill w-4 h-4 flex items-center justify-center"></i>
                    Appeler — 09 80 80 36 84
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-xl md:text-3xl text-foreground-800">Questions fréquentes</h2>
            </div>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm md:text-base text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner
          title="Store électrique en panne à Lyon ?"
          subtitle="Appelez le 09 80 80 36 84. Dépannage et pose de store électrique, devis gratuit."
          buttonText="Appeler maintenant"
          secondaryButtonText="Demander un devis"
          secondaryButtonHref="/contact/"
        />
      </main>
      <Footer />
    </>
  );
}